import { getCookie } from '@/services/helperFunctions';
import { setCookie } from '@/services/helperFunctions/serverHelperFunctions';
import { EXIST_LOCAL_STORAGE } from '../services/constants';

// Constants
const TOKEN_EXPIRATION_BUFFER = 3 * 60 * 1000; // 5 minutes buffer
const MAX_RETRY_ATTEMPTS = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 30 * 1000; // 30 seconds max

// State
let refreshTimeout: NodeJS.Timeout | null = null;
let retryCount = 0;

// Core Functions
export function startTokenRefresh() {
    stopTokenRefresh();
    validateAndScheduleRefresh();
}

export function stopTokenRefresh() {
    if (refreshTimeout) {
        clearTimeout(refreshTimeout);
        console.log('validateAndScheduleRefresh -> Token refresh cleared!');
        refreshTimeout = null;
    }
    retryCount = 0;
}

function validateAndScheduleRefresh() {
    const token = getAccessToken();
    if (!token) {
        console.log('validateAndScheduleRefresh -> no token');
        return;
    }
    try {
        const expiration = getTokenExpiration(token);
        const timeUntilRefresh = calculateRefreshTime(expiration);
        console.log('validateAndScheduleRefresh -> timeUntilRefresh', timeUntilRefresh);
        if (timeUntilRefresh <= 0) {
            immediateRefresh();
        } else {
            scheduleDelayedRefresh(timeUntilRefresh);
        }
    } catch (error) {
        handleTokenError(error as Error);
    }
}

// Helper Functions
function getTokenExpiration(token: string): number {
    const [, payload] = token.split('.');
    const { exp } = JSON.parse(atob(payload));
    return exp * 1000;
}

function calculateRefreshTime(expiresAt: number): number {
    const now = Date.now();
    const expiresIn = expiresAt - now;
    console.log('validateAndScheduleRefresh -> expiresIn', expiresIn, expiresAt, now);
    return expiresIn - TOKEN_EXPIRATION_BUFFER;
}

function immediateRefresh() {
    console.log('validateAndScheduleRefresh -> immediateRefresh');
    refreshAccessToken().catch((error) => {
        handleRefreshError(error);
    });
}

function scheduleDelayedRefresh(delay: number) {
    console.log(`validateAndScheduleRefresh -> Scheduling token refresh in ${delay / 1000} seconds`);
    refreshTimeout = setTimeout(() => {
        refreshAccessToken().catch((error) => {
            handleRefreshError(error);
        });
    }, delay);
}

// Error Handling

function handleTokenError(error: Error) {
    console.error('validateAndScheduleRefresh -> Token validation error:', error);
    // Fallback to fixed interval
    refreshTimeout = setTimeout(() => {
        refreshAccessToken().catch(handleRefreshError);
    }, 25 * 60 * 1000);
}

function handleRefreshError(error: Error) {
    console.error('validateAndScheduleRefresh -> Token refresh failed:', error);

    if (retryCount < MAX_RETRY_ATTEMPTS) {
        retryCount++;
        const retryDelay = Math.min(MAX_RETRY_DELAY, INITIAL_RETRY_DELAY * Math.pow(2, retryCount - 1));
        refreshTimeout = setTimeout(() => {
            refreshAccessToken().catch(handleRefreshError);
        }, retryDelay);
    } else {
        console.error('validateAndScheduleRefresh -> Max retry attempts reached');
        // Implement your logout or error handling logic here
        stopTokenRefresh();
    }
}

// Token Operations
async function refreshAccessToken(): Promise<void> {
    const accessToken = getCookie(EXIST_LOCAL_STORAGE.AUTHTOKEN);
    const refreshToken = getCookie(EXIST_LOCAL_STORAGE.REFRESH_TOKEN);

    if (!accessToken || !refreshToken) {
        throw new Error('validateAndScheduleRefresh -> Missing tokens for refresh');
    }

    const response = await fetch(`/nextapi/refresh-token`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ accessToken, refreshToken }),
    });

    if (!response.ok) {
        throw new Error(`validateAndScheduleRefresh -> Refresh failed with status ${response.status}`);
    }

    const json = await response.json();

    if (json && json.accessToken && json.refreshToken) {
        const newAccessToken = json.accessToken;
        const newRefreshToken = json.refreshToken;
        storeNewToken(newAccessToken, newRefreshToken);
        retryCount = 0;
        stopTokenRefresh(); // Ensure no existing timer is running
        validateAndScheduleRefresh(); // Schedule based on new token
    } else {
        stopTokenRefresh();
    }
}

function storeNewToken(accessToken: string, refreshToken?: string) {
    const expireIn = EXIST_LOCAL_STORAGE.ISSTAYLOGIN == 'true' ? 7 : null;
    setCookie(EXIST_LOCAL_STORAGE.AUTHTOKEN, accessToken, expireIn);
    setCookie(EXIST_LOCAL_STORAGE.REFRESH_TOKEN, refreshToken, expireIn);
}

function getAccessToken(): string | null {
    return getCookie(EXIST_LOCAL_STORAGE.AUTHTOKEN) || null;
}
